import {useLocation, useParams} from 'react-router-dom';
import {useGetParticipantQuery} from '../generated/graphql';
import {formatDisplayName} from '../utils/formatName';

export type BreadcrumbItem = {
  name: string;
  href: string;
  current: boolean;
};

export function useBreadcrumbs() {
  const location = useLocation();
  const params = useParams<{ userId: string }>();
  const userId = params.userId;

  const {data, loading} = useGetParticipantQuery({
    variables: {participantId: userId || ''},
    skip: !userId,
  });

  const getFullName = (): string => formatDisplayName(data?.participant?.pii, 'none');

  const getBreadcrumbs = (): BreadcrumbItem[] => {
    const paths = location.pathname.split('/').filter(Boolean);
    const items: BreadcrumbItem[] = [];

    // Dashboard route
    if (paths[0] === 'dashboard') {
      return [{name: 'Dashboard', href: '/dashboard', current: true}];
    }

    // Users routes
    if (paths[0] === 'users') {
      items.push({name: 'Users', href: '/users', current: paths.length === 1});

      if (userId) {
        if (loading) {
          items.push({name: 'Loading...', href: '#', current: false});
        } else if (data?.participant) {
          const fullName = getFullName();
          items.push({
            name: fullName || 'User Details',
            // YOJO-3434: /users/:userId has no route ("not found"); the hub's index opens the overview tab.
            href: `/users/${userId}/participant-hub`,
            current: paths.length === 2
          });

          if (paths[2] === 'wellbeing') {
            items.push({
              name: 'Wellbeing',
              href: `/users/${userId}/wellbeing`,
              current: true
            });
          }
        }
      }
    }

    // Programs routes
    if (paths[0] === 'programs') {
      items.push({name: 'Programs', href: '/programs', current: paths.length === 1});

      if (paths[1] === 'paths') {
        items.push({name: 'Learning Paths', href: '/programs/paths', current: paths.length === 2});

        if (paths[2] === 'create') {
          items.push({name: 'Create Path', href: '/programs/paths/create', current: true});
        } else if (paths[2] === 'edit' && paths[3]) {
          items.push({name: 'Edit Path', href: `/programs/paths/edit/${paths[3]}`, current: true});
        }
      } else if (paths[1] === 'journeys') {
        items.push({name: 'Learning Journeys', href: '/programs/journeys', current: paths.length === 2});

        if (paths[2] === 'create') {
          items.push({name: 'Create Journey', href: '/programs/journeys/create', current: true});
        } else if (paths[2] === 'edit' && paths[3]) {
          items.push({name: 'Edit Journey', href: `/programs/journeys/edit/${paths[3]}`, current: true});
        }
      }
    }

    return items;
  };
  return {breadcrumbs: getBreadcrumbs(), isLoading: loading};
}
